<template>
	<app-page>
		<app-heading title="Website Analytics Report" />

		<!-- Date Range Section -->
		<app-section class="mb-4">
			<app-row>
				<app-cell cols="12">
					<div class="d-flex align-center flex-wrap" style="gap: 8px;">
						<span class="mr-2 font-weight-medium">Date Range:</span>
						<input-button-group v-model="dateRangeType" color="primary" mandatory>
							<app-button no-margin small>7 Days</app-button>
							<app-button no-margin small>14 Days</app-button>
							<app-button no-margin small>MTD</app-button>
							<app-button no-margin small>Custom Range</app-button>
						</input-button-group>
						<template v-if="dateRangeType === 3">
							<input-date-picker
								v-model="customStartDate"
								label="Start Date"
								class="ml-4"
								style="max-width: 200px;"
							/>
							<input-date-picker
								v-model="customEndDate"
								label="End Date"
								class="ml-2"
								style="max-width: 200px;"
							/>
							<app-button color="primary" class="ml-2" @click="applyCustomDateRange">Apply</app-button>
						</template>
					</div>
				</app-cell>
			</app-row>
		</app-section>

		<!-- Filter Section -->
		<app-section class="mb-4">
			<app-row>
				<app-cell cols="12" sm="4" md="3">
					<input-select
						v-model="groupBy"
						:items="groupByOptions"
						label="Group By"
						multiple
					/>
				</app-cell>
				<app-cell cols="12" sm="4" md="3">
					<input-select
						v-model="filterField"
						:items="filterOptions"
						label="Filter"
					/>
				</app-cell>
				<app-cell v-if="filterField && filterField !== 'none'" cols="12" sm="4" md="4">
					<input-text
						v-model="filterValue"
						label="Filter Value"
						placeholder="Enter filter value"
						@keyup.enter="applyFilter"
					/>
				</app-cell>
				<app-cell v-if="filterField && filterField !== 'none'" cols="12" sm="2" md="2" class="d-flex align-center">
					<app-button color="primary" @click="applyFilter">Apply</app-button>
				</app-cell>
			</app-row>
		</app-section>

		<app-section v-if="loading" class="mb-4">
			<div class="text-center">
				<v-progress-circular indeterminate color="primary"></v-progress-circular>
				<div class="mt-2">Loading analytics data...</div>
			</div>
		</app-section>

		<!-- Grouped Events Tables -->
		<template v-if="groupedEventsData.length > 0">
			<app-section
				v-for="(group, groupIndex) in groupedEventsData"
				:key="`events-${groupIndex}`"
				class="mb-4"
			>
				<template v-slot:title>
					<h3>Daily Events Metrics{{ formatGroupTitle(group.groupValues) }}</h3>
				</template>
				<v-simple-table>
					<thead>
						<tr>
							<th class="text-left">Date</th>
							<th v-for="header in eventsHeaders" :key="header.value" class="text-right">
								{{ header.text }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in group.data" :key="row.date">
							<td>{{ row.date }}</td>
							<td v-for="header in eventsHeaders" :key="header.value" class="text-right">
								{{ formatValue(row[header.value], header.format) }}
							</td>
						</tr>
						<tr class="font-weight-bold" style="background-color: #f5f5f5;">
							<td>Total</td>
							<td v-for="header in eventsHeaders" :key="header.value" class="text-right">
								{{ formatValue(group.totals[header.value], header.format) }}
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</app-section>
		</template>

		<!-- Ungrouped Events Table -->
		<app-section v-if="!hasGrouping && eventsTableData.length > 0" class="mb-4">
			<template v-slot:title>
				<h3>Daily Events Metrics</h3>
			</template>
			<v-simple-table>
				<thead>
					<tr>
						<th class="text-left">Date</th>
						<th v-for="header in eventsHeaders" :key="header.value" class="text-right">
							{{ header.text }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in eventsTableData" :key="row.date">
						<td>{{ row.date }}</td>
						<td v-for="header in eventsHeaders" :key="header.value" class="text-right">
							{{ formatValue(row[header.value], header.format) }}
						</td>
					</tr>
					<tr class="font-weight-bold" style="background-color: #f5f5f5;">
						<td>Total</td>
						<td v-for="header in eventsHeaders" :key="header.value" class="text-right">
							{{ formatValue(eventsTotals[header.value], header.format) }}
						</td>
					</tr>
				</tbody>
			</v-simple-table>
		</app-section>

		<!-- Grouped USD Revenue Tables -->
		<template v-if="groupedUsdData.length > 0">
			<app-section
				v-for="(group, groupIndex) in groupedUsdData"
				:key="`usd-${groupIndex}`"
				class="mb-4"
			>
				<template v-slot:title>
					<h3>Daily Revenue USD{{ formatGroupTitle(group.groupValues) }}</h3>
				</template>
				<v-simple-table>
					<thead>
						<tr>
							<th class="text-left">Date</th>
							<th v-for="header in usdHeaders" :key="header.value" class="text-right">
								{{ header.text }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in group.data" :key="row.date">
							<td>{{ row.date }}</td>
							<td v-for="header in usdHeaders" :key="header.value" class="text-right">
								{{ formatValue(row[header.value], header.format) }}
							</td>
						</tr>
						<tr class="font-weight-bold" style="background-color: #f5f5f5;">
							<td>Total</td>
							<td v-for="header in usdHeaders" :key="header.value" class="text-right">
								{{ formatValue(group.totals[header.value], header.format) }}
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</app-section>
		</template>

		<!-- Ungrouped USD Revenue Table -->
		<app-section v-if="!hasGrouping && usdTableData.length > 0" class="mb-4">
			<template v-slot:title>
				<h3>Daily Revenue USD</h3>
			</template>
			<v-simple-table>
				<thead>
					<tr>
						<th class="text-left">Date</th>
						<th v-for="header in usdHeaders" :key="header.value" class="text-right">
							{{ header.text }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in usdTableData" :key="row.date">
						<td>{{ row.date }}</td>
						<td v-for="header in usdHeaders" :key="header.value" class="text-right">
							{{ formatValue(row[header.value], header.format) }}
						</td>
					</tr>
					<tr class="font-weight-bold" style="background-color: #f5f5f5;">
						<td>Total</td>
						<td v-for="header in usdHeaders" :key="header.value" class="text-right">
							{{ formatValue(usdTotals[header.value], header.format) }}
						</td>
					</tr>
				</tbody>
			</v-simple-table>
		</app-section>

		<!-- Grouped CAD Revenue Tables -->
		<template v-if="groupedCadData.length > 0">
			<app-section
				v-for="(group, groupIndex) in groupedCadData"
				:key="`cad-${groupIndex}`"
				class="mb-4"
			>
				<template v-slot:title>
					<h3>Daily Revenue CAD{{ formatGroupTitle(group.groupValues) }}</h3>
				</template>
				<v-simple-table>
					<thead>
						<tr>
							<th class="text-left">Date</th>
							<th v-for="header in cadHeaders" :key="header.value" class="text-right">
								{{ header.text }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in group.data" :key="row.date">
							<td>{{ row.date }}</td>
							<td v-for="header in cadHeaders" :key="header.value" class="text-right">
								{{ formatValue(row[header.value], header.format) }}
							</td>
						</tr>
						<tr class="font-weight-bold" style="background-color: #f5f5f5;">
							<td>Total</td>
							<td v-for="header in cadHeaders" :key="header.value" class="text-right">
								{{ formatValue(group.totals[header.value], header.format) }}
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</app-section>
		</template>

		<!-- Ungrouped CAD Revenue Table -->
		<app-section v-if="!hasGrouping && cadTableData.length > 0" class="mb-4">
			<template v-slot:title>
				<h3>Daily Revenue CAD</h3>
			</template>
			<v-simple-table>
				<thead>
					<tr>
						<th class="text-left">Date</th>
						<th v-for="header in cadHeaders" :key="header.value" class="text-right">
							{{ header.text }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in cadTableData" :key="row.date">
						<td>{{ row.date }}</td>
						<td v-for="header in cadHeaders" :key="header.value" class="text-right">
							{{ formatValue(row[header.value], header.format) }}
						</td>
					</tr>
					<tr class="font-weight-bold" style="background-color: #f5f5f5;">
						<td>Total</td>
						<td v-for="header in cadHeaders" :key="header.value" class="text-right">
							{{ formatValue(cadTotals[header.value], header.format) }}
						</td>
					</tr>
				</tbody>
			</v-simple-table>
		</app-section>

		<div
			v-if="!loading && !hasGrouping && eventsTableData.length === 0 && usdTableData.length === 0 && cadTableData.length === 0"
			class="text-center grey--text"
		>
			No data available for the selected period.
		</div>
		<div
			v-if="!loading && hasGrouping && groupedEventsData.length === 0 && groupedUsdData.length === 0 && groupedCadData.length === 0"
			class="text-center grey--text"
		>
			No data available for the selected period.
		</div>
	</app-page>
</template>

<script>
import moment from 'moment'

export default {
	data() {
		return {
			loading: false,
			eventsData: [],
			revenueData: [],
			filterField: 'none',
			filterValue: '',
			groupBy: [],
			dateRangeType: 0, // 0 = 7 days, 1 = 14 days, 2 = MTD, 3 = Custom Range
			customStartDate: '',
			customEndDate: '',
		}
	},
	computed: {
		filterOptions() {
			return [
				{ text: 'None', value: 'none' },
				{ text: 'Entrypoint', value: 'entrypoint' },
				{ text: 'Country', value: 'country' },
				{ text: 'Device', value: 'device' },
				{ text: 'Site', value: 'site' },
				{ text: 'Referrer', value: 'referrer' },
				{ text: 'UTM Source', value: 'utm_source' },
				{ text: 'UTM Medium', value: 'utm_medium' },
				{ text: 'UTM Campaign', value: 'utm_campaign' },
			]
		},
		groupByOptions() {
			return [
				{ text: 'Entrypoint', value: 'entrypoint' },
				{ text: 'Country', value: 'country' },
				{ text: 'Device', value: 'device' },
				{ text: 'Site', value: 'site' },
				{ text: 'Referrer', value: 'referrer' },
				{ text: 'UTM Source', value: 'utm_source' },
				{ text: 'UTM Medium', value: 'utm_medium' },
				{ text: 'UTM Campaign', value: 'utm_campaign' },
			]
		},
		hasGrouping() {
			return this.groupBy && this.groupBy.length > 0
		},
		eventsHeaders() {
			return [
				{ text: 'Page View', value: 'page_view', format: 'number' },
				{ text: 'Session', value: 'session', format: 'number' },
				{ text: 'Engagement', value: 'engagement', format: 'number' },
				{ text: 'Engagement Rate', value: 'engagement_rate', format: 'percentage' },
				{ text: 'Bounce Rate', value: 'bounce_rate', format: 'percentage' },
				{ text: 'Contact Us', value: 'contact_us', format: 'number' },
				{ text: 'Instant Quote', value: 'instant_quote', format: 'number' },
				{ text: 'Initialized Checkout', value: 'initialized_checkout', format: 'number' },
				{ text: 'Purchase', value: 'purchase', format: 'number' },
				{ text: 'Conversion Rate', value: 'conversion_rate', format: 'percentage' },
				{ text: 'Abandonment Rate', value: 'abandonment_rate', format: 'percentage' },
			]
		},
		usdHeaders() {
			return [
				{
					text: 'Gross USD', value: 'total_gross_usd', format: 'currency', currency: 'USD',
				},
				{
					text: 'Net USD', value: 'total_net_usd', format: 'currency', currency: 'USD',
				},
				{
					text: 'Lost USD', value: 'lost_usd', format: 'currency', currency: 'USD',
				},
			]
		},
		cadHeaders() {
			return [
				{
					text: 'Gross CAD', value: 'total_gross_cad', format: 'currency', currency: 'CAD',
				},
				{
					text: 'Net CAD', value: 'total_net_cad', format: 'currency', currency: 'CAD',
				},
				{
					text: 'Lost CAD', value: 'lost_cad', format: 'currency', currency: 'CAD',
				},
			]
		},
		eventsTableData() {
			// Process events data
			if (!this.eventsData || this.eventsData.length === 0) {
				return []
			}

			// Sort data by date
			const sortedData = [...this.eventsData].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())

			// Format dates and ensure all fields are present, calculate rates
			return sortedData.map((row) => {
				const session = Number(row.session) || 0
				const engagement = Number(row.engagement) || 0
				const purchase = Number(row.purchase) || 0
				const initializedCheckout = Number(row.initialized_checkout) || 0

				// Calculate rates
				const engagementRate = session > 0 ? engagement / session : 0
				const bounceRate = 1 - engagementRate
				const conversionRate = session > 0 ? purchase / session : 0
				const abandonmentRate = initializedCheckout > 0 ? 1 - (purchase / initializedCheckout) : 0

				return {
					date: moment(row.date).format('MMM DD, YYYY'),
					page_view: Number(row.page_view) || 0,
					session,
					engagement,
					engagement_rate: engagementRate,
					bounce_rate: bounceRate,
					contact_us: Number(row.contact_us) || 0,
					instant_quote: Number(row.instant_quote) || 0,
					initialized_checkout: initializedCheckout,
					purchase,
					conversion_rate: conversionRate,
					abandonment_rate: abandonmentRate,
				}
			})
		},
		usdTableData() {
			// Process USD revenue data
			if (!this.revenueData || this.revenueData.length === 0) {
				return []
			}

			// Sort data by date
			const sortedData = [...this.revenueData].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())

			// Format dates and ensure all fields are present
			return sortedData.map(row => ({
				date: moment(row.date).format('MMM DD, YYYY'),
				total_gross_usd: Number(row.total_gross_usd) || 0,
				total_net_usd: Number(row.total_net_usd) || 0,
				lost_usd: Number(row.lost_usd) || 0,
			}))
		},
		cadTableData() {
			// Process CAD revenue data
			if (!this.revenueData || this.revenueData.length === 0) {
				return []
			}

			// Sort data by date
			const sortedData = [...this.revenueData].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())

			// Format dates and ensure all fields are present
			return sortedData.map(row => ({
				date: moment(row.date).format('MMM DD, YYYY'),
				total_gross_cad: Number(row.total_gross_cad) || 0,
				total_net_cad: Number(row.total_net_cad) || 0,
				lost_cad: Number(row.lost_cad) || 0,
			}))
		},
		eventsTotals() {
			// Calculate totals for numeric fields
			const totals = {}
			this.eventsHeaders.forEach((header) => {
				if (header.format === 'percentage') {
					// Skip percentage fields - will calculate from totals below
					return
				}
				totals[header.value] = this.eventsTableData.reduce((sum, row) => sum + (Number(row[header.value]) || 0), 0)
			})

			// Calculate rates from totals
			const totalSession = totals.session || 0
			const totalEngagement = totals.engagement || 0
			const totalPurchase = totals.purchase || 0
			const totalInitializedCheckout = totals.initialized_checkout || 0

			totals.engagement_rate = totalSession > 0 ? totalEngagement / totalSession : 0
			totals.bounce_rate = 1 - totals.engagement_rate
			totals.conversion_rate = totalSession > 0 ? totalPurchase / totalSession : 0
			totals.abandonment_rate = totalInitializedCheckout > 0 ? 1 - (totalPurchase / totalInitializedCheckout) : 0

			return totals
		},
		usdTotals() {
			const totals = {}
			this.usdHeaders.forEach((header) => {
				totals[header.value] = this.usdTableData.reduce((sum, row) => sum + (Number(row[header.value]) || 0), 0)
			})
			return totals
		},
		cadTotals() {
			const totals = {}
			this.cadHeaders.forEach((header) => {
				totals[header.value] = this.cadTableData.reduce((sum, row) => sum + (Number(row[header.value]) || 0), 0)
			})
			return totals
		},
		groupedEventsData() {
			if (!this.hasGrouping || !this.eventsData || this.eventsData.length === 0) {
				return []
			}

			// Group data by groupBy fields
			const groups = {}
			this.eventsData.forEach((row) => {
				// Create a key from all groupBy field values
				const groupKey = this.getGroupKey(row, this.groupBy)
				if (!groups[groupKey]) {
					groups[groupKey] = {
						groupValues: this.getGroupValues(row, this.groupBy),
						data: [],
					}
				}
				groups[groupKey].data.push(row)
			})

			// Process each group
			return Object.values(groups).map((group) => {
				// Sort data by date
				const sortedData = [...group.data].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())

				// Format dates and calculate rates
				const processedData = sortedData.map((row) => {
					const session = Number(row.session) || 0
					const engagement = Number(row.engagement) || 0
					const purchase = Number(row.purchase) || 0
					const initializedCheckout = Number(row.initialized_checkout) || 0

					const engagementRate = session > 0 ? engagement / session : 0
					const bounceRate = 1 - engagementRate
					const conversionRate = session > 0 ? purchase / session : 0
					const abandonmentRate = initializedCheckout > 0 ? 1 - (purchase / initializedCheckout) : 0

					return {
						date: moment(row.date).format('MMM DD, YYYY'),
						page_view: Number(row.page_view) || 0,
						session,
						engagement,
						engagement_rate: engagementRate,
						bounce_rate: bounceRate,
						contact_us: Number(row.contact_us) || 0,
						instant_quote: Number(row.instant_quote) || 0,
						initialized_checkout: initializedCheckout,
						purchase,
						conversion_rate: conversionRate,
						abandonment_rate: abandonmentRate,
					}
				})

				// Calculate totals
				const totals = {}
				this.eventsHeaders.forEach((header) => {
					if (header.format === 'percentage') {
						return
					}
					totals[header.value] = processedData.reduce((sum, row) => sum + (Number(row[header.value]) || 0), 0)
				})

				const totalSession = totals.session || 0
				const totalEngagement = totals.engagement || 0
				const totalPurchase = totals.purchase || 0
				const totalInitializedCheckout = totals.initialized_checkout || 0

				totals.engagement_rate = totalSession > 0 ? totalEngagement / totalSession : 0
				totals.bounce_rate = 1 - totals.engagement_rate
				totals.conversion_rate = totalSession > 0 ? totalPurchase / totalSession : 0
				totals.abandonment_rate = totalInitializedCheckout > 0 ? 1 - (totalPurchase / totalInitializedCheckout) : 0

				return {
					groupValues: group.groupValues,
					data: processedData,
					totals,
				}
			})
		},
		groupedUsdData() {
			if (!this.hasGrouping || !this.revenueData || this.revenueData.length === 0) {
				return []
			}

			const groups = {}
			this.revenueData.forEach((row) => {
				const groupKey = this.getGroupKey(row, this.groupBy)
				if (!groups[groupKey]) {
					groups[groupKey] = {
						groupValues: this.getGroupValues(row, this.groupBy),
						data: [],
					}
				}
				groups[groupKey].data.push(row)
			})

			return Object.values(groups).map((group) => {
				const sortedData = [...group.data].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())

				const processedData = sortedData.map(row => ({
					date: moment(row.date).format('MMM DD, YYYY'),
					total_gross_usd: Number(row.total_gross_usd) || 0,
					total_net_usd: Number(row.total_net_usd) || 0,
					lost_usd: Number(row.lost_usd) || 0,
				}))

				const totals = {}
				this.usdHeaders.forEach((header) => {
					totals[header.value] = processedData.reduce((sum, row) => sum + (Number(row[header.value]) || 0), 0)
				})

				return {
					groupValues: group.groupValues,
					data: processedData,
					totals,
				}
			})
		},
		groupedCadData() {
			if (!this.hasGrouping || !this.revenueData || this.revenueData.length === 0) {
				return []
			}

			const groups = {}
			this.revenueData.forEach((row) => {
				const groupKey = this.getGroupKey(row, this.groupBy)
				if (!groups[groupKey]) {
					groups[groupKey] = {
						groupValues: this.getGroupValues(row, this.groupBy),
						data: [],
					}
				}
				groups[groupKey].data.push(row)
			})

			return Object.values(groups).map((group) => {
				const sortedData = [...group.data].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())

				const processedData = sortedData.map(row => ({
					date: moment(row.date).format('MMM DD, YYYY'),
					total_gross_cad: Number(row.total_gross_cad) || 0,
					total_net_cad: Number(row.total_net_cad) || 0,
					lost_cad: Number(row.lost_cad) || 0,
				}))

				const totals = {}
				this.cadHeaders.forEach((header) => {
					totals[header.value] = processedData.reduce((sum, row) => sum + (Number(row[header.value]) || 0), 0)
				})

				return {
					groupValues: group.groupValues,
					data: processedData,
					totals,
				}
			})
		},
		dateRange() {
			let startDate
			let endDate

			switch (this.dateRangeType) {
			case 0: // 7 days
				endDate = moment().format('YYYY-MM-DD')
				startDate = moment().subtract(6, 'days').format('YYYY-MM-DD')
				break
			case 1: // 14 days
				endDate = moment().format('YYYY-MM-DD')
				startDate = moment().subtract(13, 'days').format('YYYY-MM-DD')
				break
			case 2: // MTD
				endDate = moment().format('YYYY-MM-DD')
				startDate = moment().startOf('month').format('YYYY-MM-DD')
				break
			case 3: // Custom Range
				if (this.customStartDate && this.customEndDate) {
					startDate = moment(this.customStartDate).format('YYYY-MM-DD')
					endDate = moment(this.customEndDate).format('YYYY-MM-DD')
				} else {
					// Default to 7 days if custom dates not set
					endDate = moment().format('YYYY-MM-DD')
					startDate = moment().subtract(6, 'days').format('YYYY-MM-DD')
				}
				break
			default:
				endDate = moment().format('YYYY-MM-DD')
				startDate = moment().subtract(6, 'days').format('YYYY-MM-DD')
			}

			return { startDate, endDate }
		},
	},
	watch: {
		filterField(newValue) {
			if (newValue === 'none' || !newValue) {
				this.filterValue = ''
				// Fetch without filter when "None" is selected
				this.fetchAnalytics()
			}
		},
		groupBy() {
			// Refetch data when grouping changes
			this.fetchAnalytics()
		},
		dateRangeType() {
			// Refetch data when date range type changes (except custom)
			if (this.dateRangeType !== 3) {
				this.fetchAnalytics()
			}
		},
	},
	async mounted() {
		await this.fetchAnalytics()
	},
	methods: {
		async fetchAnalytics() {
			this.loading = true

			try {
				// Get date range from computed property
				const { startDate, endDate } = this.dateRange

				// Build filters object if filter is selected
				let filters = null
				const trimmedValue = this.filterValue ? this.filterValue.trim() : ''
				if (this.filterField && this.filterField !== 'none' && trimmedValue) {
					filters = {}
					filters[this.filterField] = trimmedValue
				}

				// Build events request
				const eventsRequest = this.$ravello.request()
					.v(2)
					.to('analytics')
					.to('websites')
					.to('events')
					.param('start_date', startDate)
					.param('end_date', endDate)
					.param('aggregation', 'daily')

				// Build revenue request
				const revenueRequest = this.$ravello.request()
					.v(2)
					.to('analytics')
					.to('websites')
					.to('revenue')
					.param('start_date', startDate)
					.param('end_date', endDate)
					.param('aggregation', 'daily')

				// Add group_by if present
				if (this.groupBy && this.groupBy.length > 0) {
					const groupByStr = Array.isArray(this.groupBy) ? this.groupBy.join(',') : this.groupBy
					eventsRequest.param('group_by', groupByStr)
					revenueRequest.param('group_by', groupByStr)
				}

				// Add filters if present
				if (filters && Object.keys(filters).length > 0) {
					const filtersJson = JSON.stringify(filters)
					eventsRequest.param('filters', filtersJson)
					revenueRequest.param('filters', filtersJson)
				}

				// Fetch events and revenue data in parallel
				const [eventsResponse, revenueResponse] = await Promise.all([
					eventsRequest.get(),
					revenueRequest.get(),
				])

				// Store raw data
				this.eventsData = eventsResponse || []
				this.revenueData = revenueResponse || []
			} catch (err) {
				this.$notifications.pushAlert('error', (err.data && err.data.message) || err.message || 'Failed to fetch analytics data')
			} finally {
				this.loading = false
			}
		},
		applyFilter() {
			this.fetchAnalytics()
		},
		applyCustomDateRange() {
			if (this.customStartDate && this.customEndDate) {
				// Validate that start date is before end date
				if (moment(this.customStartDate).isAfter(moment(this.customEndDate))) {
					this.$notifications.pushAlert('error', 'Start date must be before end date')
					return
				}
				this.fetchAnalytics()
			} else {
				this.$notifications.pushAlert('error', 'Please select both start and end dates')
			}
		},
		formatValue(value, format) {
			if (value === null || value === undefined) {
				if (format === 'currency') {
					return '$0.00'
				}
				if (format === 'percentage') {
					return '0.00%'
				}
				return '0'
			}

			if (format === 'currency') {
				// Format number with 2 decimal places
				const formattedNumber = new Intl.NumberFormat('en-US', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				}).format(value)
				// Always use $ prefix regardless of currency
				return `$${formattedNumber}`
			}

			if (format === 'percentage') {
				// Convert decimal to percentage (multiply by 100)
				const percentage = value * 100
				return `${new Intl.NumberFormat('en-US', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				}).format(percentage)}%`
			}

			// Default to number format
			return new Intl.NumberFormat('en-US').format(value)
		},
		getGroupKey(row, groupBy) {
			if (!groupBy || groupBy.length === 0) {
				return 'default'
			}
			return groupBy.map((field) => {
				const value = row[field]
				return value !== null && value !== undefined ? String(value) : 'null'
			}).join('|')
		},
		getGroupValues(row, groupBy) {
			if (!groupBy || groupBy.length === 0) {
				return {}
			}
			const values = {}
			groupBy.forEach((field) => {
				values[field] = row[field] !== null && row[field] !== undefined ? row[field] : null
			})
			return values
		},
		formatGroupTitle(groupValues) {
			if (!groupValues || Object.keys(groupValues).length === 0) {
				return ''
			}
			const parts = Object.entries(groupValues).map(([field, value]) => {
				const option = this.groupByOptions.find(opt => opt.value === field)
				const fieldLabel = option ? option.text : field
				const displayValue = value !== null && value !== undefined ? String(value) : 'null'
				return `${fieldLabel}: ${displayValue}`
			})
			return ` (${parts.join(', ')})`
		},
	},
}
</script>

